import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, Send, Save, ArrowLeft, Printer, DollarSign, FileMinus, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { addDaysISO, formatDate, formatMoney, todayISO } from '../lib/format';
import { calcLine, postInvoice, postCreditNoteSales, r2 } from '../lib/posting';
import type { Account, Contact, Invoice, InvoiceLine, TaxCode, Payment, Service } from '../lib/types';
import PaymentModal from '../components/PaymentModal';
import SendInvoiceModal from '../components/SendInvoiceModal';

interface Line {
  id: string;
  description: string;
  qty: number;
  unit_price: number;
  tax_code_id: string | null;
  account_id: string | null;
  position: number;
}

const newLine = (pos: number): Line => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  description: '',
  qty: 1,
  unit_price: 0,
  tax_code_id: null,
  account_id: null,
  position: pos,
});

export default function InvoiceEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const isNew = !id;

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);

  const [contactId, setContactId] = useState<string>('');
  const [number, setNumber] = useState<string>('');
  const [issueDate, setIssueDate] = useState<string>(todayISO());
  const [dueDate, setDueDate] = useState<string>(addDaysISO(todayISO(), 30));
  const [reference, setReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [currency, setCurrency] = useState<string>('ZAR');
  const [lines, setLines] = useState<Line[]>([newLine(0)]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [creditingNote, setCreditingNote] = useState(false);

  // Bootstrap reference data + invoice if editing.
  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const [cs, accs, tcs, svcs] = await Promise.all([
        supabase.from('dodobooks_contacts').select('*').eq('org_id', currentOrg.id).eq('is_customer', true).eq('is_archived', false).order('name'),
        supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('code'),
        supabase.from('dodobooks_tax_codes').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('code'),
        supabase.from('dodobooks_services').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).eq('is_purchase', false).order('name'),
      ]);
      setContacts((cs.data ?? []) as Contact[]);
      setAccounts((accs.data ?? []) as Account[]);
      setTaxCodes((tcs.data ?? []) as TaxCode[]);
      setServices((svcs.data ?? []) as Service[]);

      // Default tax code = STD if VAT registered, else OUT.
      const defTax = (tcs.data ?? []).find((t: any) => t.code === (currentOrg.is_vat_registered ? 'STD' : 'OUT'));
      const defAcc = (accs.data ?? []).find((a: any) => a.code === '4000');
      if (isNew) {
        setLines([{ ...newLine(0), tax_code_id: defTax?.id ?? null, account_id: defAcc?.id ?? null }]);
        // Auto-number: INV-yyyy-#### using current count + 1.
        const { count } = await supabase
          .from('dodobooks_invoices')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', currentOrg.id);
        const next = (count ?? 0) + 1;
        setNumber(`INV-${new Date().getFullYear()}-${String(next).padStart(4, '0')}`);
      }
    })();
  }, [currentOrg, isNew]);

  useEffect(() => {
    if (!id || !currentOrg) return;
    (async () => {
      const { data: inv } = await supabase.from('dodobooks_invoices').select('*').eq('id', id).maybeSingle();
      if (inv) {
        setInvoice(inv as Invoice);
        setContactId((inv as any).contact_id);
        setNumber((inv as any).number);
        setIssueDate((inv as any).issue_date);
        setDueDate((inv as any).due_date);
        setReference((inv as any).reference ?? '');
        setNotes((inv as any).notes ?? '');
        setCurrency((inv as any).currency);
      }
      const { data: ls } = await supabase
        .from('dodobooks_invoice_lines')
        .select('*')
        .eq('invoice_id', id)
        .order('position');
      if (ls?.length) {
        setLines(ls.map((l: any) => ({
          id: l.id, description: l.description, qty: Number(l.qty), unit_price: Number(l.unit_price),
          tax_code_id: l.tax_code_id, account_id: l.account_id, position: l.position,
        })));
      }
      const { data: pays } = await supabase
        .from('dodobooks_payments')
        .select('*')
        .eq('invoice_id', id)
        .order('payment_date', { ascending: false });
      setPayments((pays ?? []) as Payment[]);
    })();
  }, [id, currentOrg]);

  async function refreshAfterPosting() {
    if (!id) return;
    const { data } = await supabase.from('dodobooks_invoices').select('*').eq('id', id).maybeSingle();
    if (data) setInvoice(data as Invoice);
    const { data: pays } = await supabase
      .from('dodobooks_payments')
      .select('*')
      .eq('invoice_id', id)
      .order('payment_date', { ascending: false });
    setPayments((pays ?? []) as Payment[]);
  }

  async function issueCreditNote() {
    if (!currentOrg || !invoice) return;
    if (!confirm(`Issue a credit note for the outstanding balance on ${invoice.number}? This reverses the invoice's revenue and VAT on the ledger.`)) return;
    setCreditingNote(true); setErr(null); setSavedMsg(null);

    const { count } = await supabase
      .from('dodobooks_invoices')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', currentOrg.id)
      .eq('kind', 'credit_note');
    const cnNum = `CN-${new Date().getFullYear()}-${String((count ?? 0) + 1).padStart(4, '0')}`;
    const outstanding = r2(Number(invoice.total) - Number(invoice.amount_paid));

    // Insert credit-note header + lines copied from the invoice
    const { data: cn, error: ce } = await supabase
      .from('dodobooks_invoices')
      .insert({
        org_id: currentOrg.id,
        contact_id: invoice.contact_id,
        number: cnNum,
        issue_date: todayISO(),
        due_date: todayISO(),
        reference: `Credit note for ${invoice.number}`,
        status: 'draft',
        currency: invoice.currency,
        subtotal: 0, tax_total: 0, total: 0, amount_paid: 0,
        kind: 'credit_note',
        applied_to_invoice_id: invoice.id,
        notes: `Reverses invoice ${invoice.number} (outstanding ${formatMoney(outstanding, invoice.currency)}).`,
      })
      .select('*')
      .single();
    if (ce || !cn) { setCreditingNote(false); setErr(ce?.message ?? 'Failed to create credit note'); return; }

    const cnLines = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return {
        invoice_id: (cn as any).id,
        description: l.description,
        qty: l.qty || 0,
        unit_price: l.unit_price || 0,
        tax_code_id: l.tax_code_id,
        account_id: l.account_id,
        line_total: r2(lineTotal),
        position: i,
      };
    });
    if (cnLines.length > 0) await supabase.from('dodobooks_invoice_lines').insert(cnLines);

    const { data: cnFresh } = await supabase.from('dodobooks_invoices').select('*').eq('id', (cn as any).id).single();
    const { data: cnLineRows } = await supabase.from('dodobooks_invoice_lines').select('*').eq('invoice_id', (cn as any).id).order('position');
    const result = await postCreditNoteSales({
      org_id: currentOrg.id,
      invoice: cnFresh as Invoice,
      lines: (cnLineRows ?? []) as InvoiceLine[],
      accounts,
      taxCodes,
      applies_to_invoice: invoice,
    });
    setCreditingNote(false);
    if (result.error) { setErr(result.error); return; }
    setSavedMsg(`Credit note ${cnNum} issued.`);
    refreshAfterPosting();
    setTimeout(() => navigate(`/invoices/${(cn as any).id}`), 800);
  }

  const status = invoice?.status ?? 'draft';
  const isCreditNote = invoice?.kind === 'credit_note';
  const readOnly = status !== 'draft';
  const outstanding = invoice ? r2(Number(invoice.total) - Number(invoice.amount_paid)) : 0;
  const canRecordPayment = !isCreditNote && readOnly && status !== 'paid' && status !== 'void' && outstanding > 0.005;
  const canIssueCreditNote = !isCreditNote && readOnly && status !== 'void' && outstanding > 0.005;

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const l of lines) {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const rate = tc?.rate ?? 0;
      const { net, tax: lineTax } = calcLine(l.qty || 0, l.unit_price || 0, rate);
      subtotal += net;
      tax += lineTax;
    }
    return { subtotal: r2(subtotal), tax: r2(tax), total: r2(subtotal + tax) };
  }, [lines, taxCodes]);

  function updateLine(i: number, patch: Partial<Line>) {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  }
  function removeLine(i: number) {
    setLines((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));
  }
  function addLine() {
    const last = lines[lines.length - 1];
    setLines((prev) => [...prev, { ...newLine(prev.length), tax_code_id: last?.tax_code_id ?? null, account_id: last?.account_id ?? null }]);
  }

  // Pick a service: autofill description, qty=1, unit_price, account, tax.
  function pickService(i: number, serviceId: string) {
    if (!serviceId) return;
    const s = services.find((x) => x.id === serviceId);
    if (!s) return;
    updateLine(i, {
      description: s.description || s.name,
      qty: 1,
      unit_price: Number(s.default_price) || 0,
      account_id: s.default_account_id,
      tax_code_id: s.default_tax_code_id,
    });
  }

  
  async function persistDraft(): Promise<string | null> {
    if (!currentOrg) return null;
    if (!contactId) { setErr('Pick a customer first.'); return null; }
    const payload = {
      org_id: currentOrg.id,
      contact_id: contactId,
      number,
      issue_date: issueDate,
      due_date: dueDate,
      reference: reference || null,
      status: status === 'draft' ? 'draft' : status,
      currency,
      subtotal: totals.subtotal,
      tax_total: totals.tax,
      total: totals.total,
      notes: notes || null,
    };
    let invId = id ?? null;
    if (invId) {
      const { error } = await supabase.from('dodobooks_invoices').update(payload).eq('id', invId);
      if (error) { setErr(error.message); return null; }
    } else {
      const { data, error } = await supabase.from('dodobooks_invoices').insert(payload).select('id').single();
      if (error || !data) { setErr(error?.message ?? 'Insert failed'); return null; }
      invId = data.id;
    }
    // Reset lines: delete existing, insert current.
    await supabase.from('dodobooks_invoice_lines').delete().eq('invoice_id', invId);
    const lineRows = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return {
        invoice_id: invId,
        description: l.description,
        qty: l.qty || 0,
        unit_price: l.unit_price || 0,
        tax_code_id: l.tax_code_id,
        account_id: l.account_id,
        line_total: r2(lineTotal),
        position: i,
      };
    });
    if (lineRows.length > 0) {
      const { error } = await supabase.from('dodobooks_invoice_lines').insert(lineRows);
      if (error) { setErr(error.message); return null; }
    }
    return invId;
  }

  async function onSave() {
    setBusy(true); setErr(null); setSavedMsg(null);
    const newId = await persistDraft();
    setBusy(false);
    if (newId) {
      setSavedMsg('Draft saved.');
      if (!id) navigate(`/invoices/${newId}`, { replace: true });
    }
  }

  async function onSend() {
    if (!currentOrg) return;
    setBusy(true); setErr(null); setSavedMsg(null);
    const newId = await persistDraft();
    if (!newId) { setBusy(false); return; }
    const { data: invFresh } = await supabase.from('dodobooks_invoices').select('*').eq('id', newId).maybeSingle();
    const { data: linesFresh } = await supabase.from('dodobooks_invoice_lines').select('*').eq('invoice_id', newId).order('position');
    if (!invFresh || !linesFresh) { setBusy(false); setErr('Could not reload after save.'); return; }
    const result = await postInvoice({
      org_id: currentOrg.id,
      invoice: invFresh as Invoice,
      lines: linesFresh as InvoiceLine[],
      accounts,
      taxCodes,
    });
    setBusy(false);
    if (result.error) setErr(result.error);
    else {
      setSavedMsg('Invoice sent and posted to the ledger.');
      if (!id) navigate(`/invoices/${newId}`, { replace: true });
      else {
        // Refresh
        const { data } = await supabase.from('dodobooks_invoices').select('*').eq('id', newId).maybeSingle();
        setInvoice(data as Invoice);
      }
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/invoices" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
          <div>
            <h1 className="h-page">
            {isCreditNote && <span className="font-mono text-xs uppercase tracking-widest text-gold mr-2 align-middle">Credit note</span>}
            {number || 'New invoice'}
          </h1>
            <p className="text-text-dim text-sm mt-1">
              {readOnly ? <span className={`pill pill-${status}`}>{status}</span> : 'Editing draft'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && (
            <>
              <button onClick={onSave} disabled={busy} className="btn btn-secondary disabled:opacity-50"><Save size={14} /> Save draft</button>
              <button onClick={onSend} disabled={busy} className="btn btn-primary disabled:opacity-50"><Send size={14} /> Send & post</button>
            </>
          )}
          {readOnly && invoice && (
            <button onClick={() => setShowSend(true)} className="btn btn-primary"><Share2 size={14} /> Send</button>
          )}
          {readOnly && invoice?.public_token && (
            <button
              type="button"
              onClick={() => window.open(`/app/p/invoice/${invoice.public_token}`, '_blank', 'noopener')}
              className="btn btn-secondary"
              title="Opens the branded customer-facing invoice — uses your saved template"
            >
              <Printer size={14} /> View / Print
            </button>
          )}
          {canRecordPayment && (
            <button onClick={() => setShowPayment(true)} className="btn btn-primary"><DollarSign size={14} /> Record payment</button>
          )}
          {canIssueCreditNote && (
            <button onClick={issueCreditNote} disabled={creditingNote} className="btn btn-secondary disabled:opacity-50">
              <FileMinus size={14} /> {creditingNote ? 'Issuing…' : 'Issue credit note'}
            </button>
          )}
        </div>
      </header>

      {savedMsg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{savedMsg}</div>}
      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

      <div className="card grid grid-cols-2 gap-4">
        <div className="field col-span-2">
          <label>Customer *</label>
          <select disabled={readOnly} required value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">— pick a customer —</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Invoice number</label><input disabled={readOnly} value={number} onChange={(e) => setNumber(e.target.value)} /></div>
        <div className="field"><label>Reference</label><input disabled={readOnly} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO number, etc." /></div>
        <div className="field"><label>Issue date</label><input disabled={readOnly} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></div>
        <div className="field"><label>Due date</label><input disabled={readOnly} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
        <div className="field"><label>Currency</label>
          <select disabled={readOnly} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option>ZAR</option><option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table>
          <thead>
            <tr>
              <th className="px-4">Description</th>
              <th className="w-20">Qty</th>
              <th className="w-32">Unit price</th>
              <th className="w-48">Account</th>
              <th className="w-32">Tax</th>
              <th className="w-32 text-right">Line total</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const tc = taxCodes.find((t) => t.id === l.tax_code_id);
              const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
              return (
                <tr key={l.id}>
                  <td className="px-4">
                    {!readOnly && services.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => { pickService(i, e.target.value); e.currentTarget.value = ''; }}
                        className="w-full text-[11px] mb-1 bg-emerald/5 border border-emerald/20 text-emerald-deep"
                        title="Pick a saved service to auto-fill this line"
                      >
                        <option value="">＋ Pick a service…</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}{s.code ? ' (' + s.code + ')' : ''} — R{Number(s.default_price).toFixed(2)}</option>
                        ))}
                      </select>
                    )}
                    <input disabled={readOnly} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Item or service…" className="w-full" />
                  </td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.qty} onChange={(e) => updateLine(i, { qty: Number(e.target.value) })} className="w-full" /></td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} className="w-full" /></td>
                  <td>
                    <select disabled={readOnly} value={l.account_id ?? ''} onChange={(e) => updateLine(i, { account_id: e.target.value || null })} className="w-full">
                      <option value="">— pick account —</option>
                      {accounts.filter((a) => a.account_type === 'revenue').map((a) => (
                        <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select disabled={readOnly} value={l.tax_code_id ?? ''} onChange={(e) => updateLine(i, { tax_code_id: e.target.value || null })} className="w-full">
                      <option value="">No VAT</option>
                      {taxCodes.filter((t) => t.is_output).map((t) => (
                        <option key={t.id} value={t.id}>{t.code} ({(t.rate * 100).toFixed(0)}%)</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-right num">{formatMoney(lineTotal, currency)}</td>
                  <td>
                    {!readOnly && (
                      <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} className="text-text-mute hover:text-rust disabled:opacity-30">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (
          <div className="border-t border-line p-3">
            <button type="button" onClick={addLine} className="btn btn-ghost"><Plus size={14} /> Add line</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <label className="block mb-1">Notes</label>
          <textarea disabled={readOnly} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" placeholder="Visible to your customer on the printed invoice." />
        </div>
        <div className="card">
          <div className="flex justify-between py-1.5 text-sm text-text-dim">
            <span>Subtotal</span>
            <span className="num text-text">{formatMoney(totals.subtotal, currency)}</span>
          </div>
          <div className="flex justify-between py-1.5 text-sm text-text-dim border-b border-line">
            <span>VAT</span>
            <span className="num text-text">{formatMoney(totals.tax, currency)}</span>
          </div>
          <div className="flex justify-between py-2 font-serif text-lg">
            <span>Total</span>
            <span className="num text-emerald">{formatMoney(totals.total, currency)}</span>
          </div>
          {invoice && Number(invoice.amount_paid) > 0 && (
            <div className="flex justify-between py-1.5 text-sm text-text-dim">
              <span>Paid</span>
              <span className="num text-text">{formatMoney(invoice.amount_paid, currency)}</span>
            </div>
          )}
          {invoice && (
            <div className="flex justify-between py-1.5 text-sm">
              <span>Outstanding</span>
              <span className="num text-gold">{formatMoney(Number(invoice.total) - Number(invoice.amount_paid), currency)}</span>
            </div>
          )}
        </div>
      </div>

      {payments.length > 0 && (
        <div className="card p-0">
          <div className="px-5 py-3 border-b border-line"><h3 className="h-section text-base">Payments received</h3></div>
          <table>
            <thead>
              <tr><th className="px-5">Date</th><th>Reference</th><th className="text-right pr-5">Amount</th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td className="px-5">{formatDate(p.payment_date)}</td>
                  <td className="text-text-dim">{p.reference || '—'}</td>
                  <td className="text-right num pr-5 text-emerald">{formatMoney(p.amount, invoice?.currency ?? 'ZAR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {invoice && invoice.journal_id && (
        <p className="text-xs text-text-mute font-mono">
          Posted to journal {invoice.journal_id.slice(0, 8)} on {formatDate(invoice.issue_date)} · source={isCreditNote ? 'credit-note' : 'invoice'}
        </p>
      )}

      {showPayment && invoice && (
        <PaymentModal
          direction="received"
          invoice={invoice}
          onClose={() => setShowPayment(false)}
          onPosted={() => { setShowPayment(false); setSavedMsg('Payment recorded and posted.'); refreshAfterPosting(); }}
        />
      )}

      {showSend && invoice && (() => {
        const c = contacts.find((x) => x.id === invoice.contact_id);
        return c ? <SendInvoiceModal invoice={invoice} contact={c} onClose={() => setShowSend(false)} /> : null;
      })()}

    </div>
  );
}
